<template>
  <section class="watchlist-creator">
    <div v-if="watchlistId && isCurrentEditor" class="editor-owner-strip">
      You are the editor — please save when done.
    </div>

    <header class="creator-header">
      <div
        class="creator-actions"
        :class="{ 'actions-only': !watchlistId }"
      >
        <div v-if="watchlistId" class="editor-presence">
          <span class="editor-avatar">{{ editorInitials }}</span>
          <span>{{ editorStatus }}</span>
        </div>

        <div class="creator-buttons">
          <button type="button" class="cancel-button" :disabled="saving" @click="cancelComposer">
            Cancel
          </button>

          <button
            type="button"
            class="review-button"
            :disabled="saving || !canSetForReview || editingBlocked"
            @click="setForReview"
          >
            Set for Review
          </button>
        </div>
      </div>
    </header>

    <form id="watchlist-creator-form" class="creator-form" @submit.prevent="saveDraft">
      <fieldset class="edit-fields" :disabled="editingBlocked">
        <section class="form-card">
          <h4>Watchlist Information</h4>

          <div class="field-grid">
            <label class="field-group full-width">
              <span>Watchlist Name</span>
              <input v-model="name" type="text" autocomplete="off" />
            </label>

            <label class="field-group full-width">
              <span>Description</span>
              <textarea v-model="description" rows="5" />
            </label>

            <LitigationSearchMetadataInput
              v-model="tags"
              class="full-width"
              label="Tags"
              placeholder="Consumer services or consumer services, billing, subscriptions"
              hint="User-facing categories."
              creator-mode
            />

            <LitigationSearchMetadataInput
              v-model="searchTerms"
              class="full-width"
              label="Search Terms"
              placeholder="Company name or company name, product, common complaint phrase"
              hint="Hidden search aliases."
              creator-mode
            />

            <label class="field-group full-width">
              <span>Explainer Title</span>
              <input v-model="explainerTitle" type="text" autocomplete="off" />
            </label>

            <label class="field-group full-width">
              <span>Explainer Description</span>
              <textarea v-model="explainerDescription" rows="8" />
            </label>
          </div>
        </section>

        <WatchlistQuestionBuilder
          title="Watchlist Questions"
          description="Questions used to understand what happened and identify possible patterns."
          :questions="questions"
          @add="addQuestion"
          @remove="removeQuestion"
          @move-up="moveQuestionUp"
          @move-down="moveQuestionDown"
          @restore-base="restoreBaseQuestions"
        />

        <section class="form-card">
          <h4>Banner Image</h4>

          <div class="banner-input-row">
            <input
              ref="bannerInput"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              @change="handleFileSelection"
            />

            <button
              v-if="selectedFileName || hasSavedBanner"
              type="button"
              class="remove-banner-button"
              :disabled="saving"
              @click="clearBannerSelection"
            >
              <span class="material-icons-outlined" aria-hidden="true">delete_outline</span>
            </button>
          </div>

          <span v-if="selectedFileName" class="banner-status">{{ selectedFileName }}</span>
          <span v-else-if="hasSavedBanner" class="banner-status">Banner saved</span>
          <span v-else-if="bannerRemovalRequested" class="banner-status removal-pending">
            Banner will be removed when this draft is saved.
          </span>

          <img v-if="currentBannerUrl && !bannerRemovalRequested" :src="currentBannerUrl" class="banner-preview" alt="Watchlist banner preview" />
        </section>
      </fieldset>

      <p v-if="editingBlocked" class="editing-message">
        {{ leaseError || `${editorName} is currently editing this Watchlist. This view is read-only.` }}
      </p>

      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

      <section v-if="watchlistId" class="watchlist-removal">
        <button
          v-if="!removalArmed"
          type="button"
          class="arm-removal-button"
          :disabled="saving || removing || editingBlocked"
          @click="beginRemoval"
        >
          Remove Watchlist
        </button>

        <div v-else class="removal-confirmation">
          <input
            v-model="removalConfirmation"
            type="text"
            autocomplete="off"
            placeholder="Type CONFIRM DELETE"
            :disabled="removing"
            @keyup.enter="removeWatchlistDraft"
          />
          <button
            type="button"
            class="confirm-removal-button"
            :disabled="!canConfirmRemoval || removing"
            @click="removeWatchlistDraft"
          >
            {{ removing ? "Removing..." : "Submit Removal" }}
          </button>
          <button
            type="button"
            class="cancel-removal-button"
            :disabled="removing"
            @click="cancelRemoval"
          >
            Cancel
          </button>
        </div>
      </section>
    </form>

    <div class="save-bar">
      <span class="save-status">{{ saveStatus }}</span>
      <button
        type="submit"
        form="watchlist-creator-form"
        class="save-button"
        :disabled="saving || !isDirty || !canSaveDraft || editingBlocked"
      >
        {{ saving ? "Saving..." : "Save Draft" }}
      </button>
    </div>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import WatchlistQuestionBuilder from "@/components/watchlists/WatchlistQuestionBuilder.vue";
import LitigationSearchMetadataInput from "@/components/litigations/LitigationSearchMetadataInput.vue";
import { useWatchlistEditLease } from "@/composables/useWatchlistEditLease";
import { deleteWatchlist, saveWatchlist, updateWatchlist } from "@/firebase/firestore";
import { deleteFile, uploadFile } from "@/firebase/storage";
import {
  cleanWatchlistQuestions,
  cloneWatchlistQuestions,
  createBaseWatchlistQuestions,
  createWatchlistQuestion
} from "@/models/watchlistQuestions";
import { normalizeSearchMetadata } from "@/models/searchMetadata";

const defaultExplainerDescription =
  "Watchlist lets you report problems or frustrations with companies, products, or services. Reports are reviewed together to identify patterns or trends that may suggest broader issues.\n\nImportant to know: Submitting a Watchlist report does not create a legal case, does not retain an attorney, and does not mean a lawsuit exists. Information is reviewed only in combination with other reports to look for trends. If a potential legal matter emerges, we may contact you with general information. You are never obligated to participate, and you can always choose to contact an attorney directly through the Maix app for immediate legal help.";

export default {
  name: "WatchlistCreator",
  components: { WatchlistQuestionBuilder, LitigationSearchMetadataInput },
  props: {
    watchlist: { type: Object, default: null },
    adminId: { type: String, default: "" },
    adminName: { type: String, default: "Admin" }
  },
  emits: ["cancel", "draft-saved", "removed", "review-submitted"],

  setup(props, { emit }) {
    const name = ref(props.watchlist?.name ?? "");
    const description = ref(props.watchlist?.description ?? "");
    const tags = ref(normalizeSearchMetadata(props.watchlist?.tags));
    const searchTerms = ref(normalizeSearchMetadata(props.watchlist?.searchTerms));
    const explainerTitle = ref(props.watchlist?.explainerTitle ?? "Watchlist");
    const explainerDescription = ref(
      props.watchlist?.explainerDescription ?? defaultExplainerDescription
    );
    const questions = ref(
      props.watchlist ?
        cloneWatchlistQuestions(props.watchlist.questions) :
        createBaseWatchlistQuestions()
    );
    const watchlistId = ref(props.watchlist?.watchlistId ?? props.watchlist?.id ?? "");
    const currentBannerUrl = ref(props.watchlist?.bannerUrl ?? "");
    const hasSavedBanner = ref(Boolean(currentBannerUrl.value));
    const selectedFile = ref(null);
    const selectedFileName = ref("");
    const bannerInput = ref(null);
    const bannerRemovalRequested = ref(false);
    const saving = ref(false);
    const removing = ref(false);
    const removalArmed = ref(false);
    const removalConfirmation = ref("");
    const errorMessage = ref("");
    const reorderRevision = ref(0);

    const {
      editingBlocked,
      editorInitials,
      editorName,
      editorStatus,
      isCurrentEditor,
      leaseError
    } = useWatchlistEditLease({
      watchlistId,
      adminId: computed(() => props.adminId),
      adminName: computed(() => props.adminName)
    });

    const formSnapshot = computed(() => JSON.stringify({
      name: name.value,
      description: description.value,
      tags: tags.value,
      searchTerms: searchTerms.value,
      explainerTitle: explainerTitle.value,
      explainerDescription: explainerDescription.value,
      questions: questions.value,
      selectedFile: selectedFile.value ? {
        name: selectedFile.value.name,
        size: selectedFile.value.size,
        lastModified: selectedFile.value.lastModified
      } : null,
      bannerRemovalRequested: bannerRemovalRequested.value,
      reorderRevision: reorderRevision.value
    }));
    const lastSavedSnapshot = ref(formSnapshot.value);
    const isDirty = computed(() => formSnapshot.value !== lastSavedSnapshot.value);
    const cleanedQuestions = computed(() => cleanWatchlistQuestions(questions.value));
    const canSaveDraft = computed(() =>
      name.value.trim() !== "" &&
      description.value.trim() !== "" &&
      explainerTitle.value.trim() !== "" &&
      explainerDescription.value.trim() !== ""
    );
    const canSetForReview = computed(() =>
      canSaveDraft.value &&
      cleanedQuestions.value.length > 0 &&
      (hasSavedBanner.value || selectedFile.value !== null)
    );
    const canConfirmRemoval = computed(() =>
      removalConfirmation.value === "CONFIRM DELETE" && !editingBlocked.value
    );
    const saveStatus = computed(() => {
      if (saving.value) { return "Saving draft..."; }
      if (isDirty.value && !canSaveDraft.value) {
        return "Complete the required fields to save.";
      }
      if (isDirty.value) { return "Unsaved changes"; }
      return watchlistId.value ? "Draft saved" : "No changes to save";
    });

    const addQuestion = () => questions.value.push(createWatchlistQuestion());
    const removeQuestion = index => questions.value.splice(index, 1);
    const moveQuestion = (index, direction) => {
      const destination = index + direction;
      if (destination < 0 || destination >= questions.value.length) { return; }
      const [question] = questions.value.splice(index, 1);
      questions.value.splice(destination, 0, question);
      reorderRevision.value += 1;
    };
    const moveQuestionUp = index => moveQuestion(index, -1);
    const moveQuestionDown = index => moveQuestion(index, 1);
    const restoreBaseQuestions = () => {
      if (!window.confirm("Replace the current questions with the base Watchlist question set?")) {
        return;
      }
      questions.value = createBaseWatchlistQuestions();
    };

    const handleFileSelection = event => {
      const file = event.target.files?.[0] ?? null;
      selectedFile.value = file;
      selectedFileName.value = file?.name ?? "";
      if (file) { bannerRemovalRequested.value = false; }
    };

    const clearBannerSelection = () => {
      if (selectedFile.value) {
        selectedFile.value = null;
        selectedFileName.value = "";
        if (bannerInput.value) { bannerInput.value.value = ""; }
        return;
      }

      if (hasSavedBanner.value) {
        hasSavedBanner.value = false;
        bannerRemovalRequested.value = true;
      }
    };

    const watchlistData = publicationStatus => ({
      schemaVersion: 1,
      name: name.value.trim(),
      description: description.value.trim(),
      tags: normalizeSearchMetadata(tags.value),
      searchTerms: normalizeSearchMetadata(searchTerms.value),
      explainerTitle: explainerTitle.value.trim(),
      explainerDescription: explainerDescription.value.trim(),
      bannerUrl: bannerRemovalRequested.value ? "" : currentBannerUrl.value,
      publicationStatus,
      questions: cleanedQuestions.value
    });

    const persistDraft = async () => {
      const data = watchlistData("draft");
      const savedId = watchlistId.value || await saveWatchlist(data);
      if (!savedId) { throw new Error("Watchlist was not saved."); }

      if (watchlistId.value) {
        const updated = await updateWatchlist(savedId, data);
        if (!updated) { throw new Error("Watchlist was not updated."); }
      } else {
        watchlistId.value = savedId;
      }

      if (selectedFile.value) {
        const extension = selectedFile.value.name.split(".").pop() ?? "png";
        const path = `WatchFeed/${savedId}/banner.${extension}`;
        const bannerUrl = await uploadFile(selectedFile.value, path);
        const updated = await updateWatchlist(savedId, { bannerUrl });
        if (!updated) { throw new Error("Watchlist banner was not saved."); }
        currentBannerUrl.value = bannerUrl;
        hasSavedBanner.value = true;
        selectedFile.value = null;
        selectedFileName.value = "";
        if (bannerInput.value) { bannerInput.value.value = ""; }
      } else if (bannerRemovalRequested.value) {
        const oldBannerUrl = currentBannerUrl.value;
        const updated = await updateWatchlist(savedId, { bannerUrl: "" });
        if (!updated) { throw new Error("Watchlist banner was not removed."); }
        if (oldBannerUrl) { await deleteFile(oldBannerUrl); }

        currentBannerUrl.value = "";
        bannerRemovalRequested.value = false;
      }

      lastSavedSnapshot.value = formSnapshot.value;
      return savedId;
    };

    const saveDraft = async () => {
      if (!canSaveDraft.value || !isDirty.value || saving.value || editingBlocked.value) {
        return;
      }

      saving.value = true;
      errorMessage.value = "";
      try {
        emit("draft-saved", await persistDraft());
      } catch (error) {
        console.error("❌ Unable to save Watchlist draft:", error);
        errorMessage.value = "Unable to save the Watchlist draft. Please try again.";
      } finally {
        saving.value = false;
      }
    };

    const setForReview = async () => {
      if (!canSetForReview.value || saving.value || editingBlocked.value) { return; }

      saving.value = true;
      errorMessage.value = "";
      try {
        const savedId = isDirty.value || !watchlistId.value ?
          await persistDraft() : watchlistId.value;
        const updated = await updateWatchlist(savedId, watchlistData("inReview"));
        if (!updated) { throw new Error("Watchlist was not set for review."); }
        emit("review-submitted", savedId);
      } catch (error) {
        console.error("❌ Unable to set Watchlist for review:", error);
        errorMessage.value = "Unable to set the Watchlist for review. Please try again.";
      } finally {
        saving.value = false;
      }
    };

    const beginRemoval = () => {
      if (editingBlocked.value || saving.value) { return; }
      removalConfirmation.value = "";
      removalArmed.value = true;
    };
    const cancelRemoval = () => {
      if (removing.value) { return; }
      removalConfirmation.value = "";
      removalArmed.value = false;
    };
    const removeWatchlistDraft = async () => {
      if (!canConfirmRemoval.value || removing.value || !watchlistId.value) {
        return;
      }

      removing.value = true;
      errorMessage.value = "";
      try {
        const removed = await deleteWatchlist(watchlistId.value);
        if (!removed) { throw new Error("Watchlist was not removed."); }
        if (currentBannerUrl.value) { await deleteFile(currentBannerUrl.value); }
        emit("removed", watchlistId.value);
      } catch (error) {
        console.error("❌ Unable to remove Watchlist:", error);
        errorMessage.value =
          "Unable to remove this Draft Watchlist. Watchlists with submissions cannot be removed.";
      } finally {
        removing.value = false;
      }
    };

    const cancelComposer = () => {
      if (isDirty.value && !window.confirm("Discard your unsaved Watchlist changes?")) {
        return;
      }
      emit("cancel");
    };
    const warnBeforeUnload = event => {
      if (!isDirty.value || saving.value) { return; }
      event.preventDefault();
      event.returnValue = "";
    };
    onMounted(() => window.addEventListener("beforeunload", warnBeforeUnload));
    onBeforeUnmount(() => window.removeEventListener("beforeunload", warnBeforeUnload));

    return {
      name,
      description,
      tags,
      searchTerms,
      explainerTitle,
      explainerDescription,
      questions,
      watchlistId,
      currentBannerUrl,
      hasSavedBanner,
      selectedFileName,
      bannerInput,
      bannerRemovalRequested,
      saving,
      removing,
      removalArmed,
      removalConfirmation,
      errorMessage,
      editingBlocked,
      editorInitials,
      editorName,
      editorStatus,
      isCurrentEditor,
      leaseError,
      isDirty,
      canSaveDraft,
      canSetForReview,
      canConfirmRemoval,
      saveStatus,
      addQuestion,
      removeQuestion,
      moveQuestionUp,
      moveQuestionDown,
      restoreBaseQuestions,
      handleFileSelection,
      clearBannerSelection,
      cancelComposer,
      beginRemoval,
      cancelRemoval,
      removeWatchlistDraft,
      saveDraft,
      setForReview
    };
  }
};
</script>

<style scoped>
.watchlist-creator {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #fbfcfb;
}

.editor-owner-strip {
  padding: 8px 16px;
  flex-shrink: 0;
  background: #18ab69;
  color: #ffffff;
  font-size: 0.86rem;
  font-weight: 700;
  text-align: center;
}

.creator-header {
  display: flex;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #e2e7e4;
  background: #ffffff;
}

.creator-actions {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.creator-actions.actions-only { justify-content: flex-end; }
.creator-buttons, .editor-presence, .banner-input-row { display: flex; align-items: center; gap: 12px; }
.editor-presence { color: #66716b; font-size: 0.84rem; white-space: nowrap; }
.editor-avatar {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 50%;
  background: #e9f5ef;
  color: #148254;
  font-weight: 800;
}

.creator-form { min-height: 0; padding: 24px 24px 110px; overflow-y: auto; }
.edit-fields { display: flex; flex-direction: column; gap: 24px; min-width: 0; margin: 0; padding: 0; border: 0; }
.edit-fields:disabled { opacity: 0.72; }
.form-card { display: flex; flex-direction: column; gap: 18px; padding: 20px; border: 1px solid #e2e7e4; border-radius: 12px; background: #ffffff; }
.form-card h4 { margin: 0; color: #25322c; }
.field-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.full-width { grid-column: 1 / -1; }
.field-group { display: flex; flex-direction: column; gap: 8px; color: #28342e; font-weight: 600; }
.field-group input, .field-group textarea { width: 100%; box-sizing: border-box; padding: 12px 13px; border: 1px solid #d7ded9; border-radius: 9px; font: inherit; }
.field-group textarea { resize: vertical; line-height: 1.5; }
.banner-input-row input { flex: 1; }
.remove-banner-button { display: grid; width: 42px; height: 42px; place-items: center; border: 1px solid #efc5c5; border-radius: 9px; background: #fff7f7; color: #b53b3b; cursor: pointer; }
.banner-status { color: #66716b; font-size: 0.86rem; }
.removal-pending { color: #a15c17; }
.banner-preview { width: 100%; max-width: 760px; max-height: 280px; object-fit: cover; border-radius: 12px; }
.editing-message, .error-message { margin: 18px 0 0; padding: 12px 14px; border-radius: 9px; }
.editing-message { border: 1px solid #ead39f; background: #fffaf0; color: #805b12; }
.error-message { border: 1px solid #efc5c5; background: #fff7f7; color: #a33636; }
.watchlist-removal { margin-top: 24px; padding-top: 20px; border-top: 1px solid #e2e7e4; }
.arm-removal-button, .confirm-removal-button, .cancel-removal-button { padding: 10px 15px; border-radius: 9px; font: inherit; font-weight: 700; cursor: pointer; }
.arm-removal-button { border: 1px solid #efc5c5; background: #fff7f7; color: #a33636; }
.removal-confirmation { display: flex; align-items: center; gap: 10px; }
.removal-confirmation input { min-width: 220px; padding: 10px 12px; border: 1px solid #d7ded9; border-radius: 9px; font: inherit; }
.confirm-removal-button { border: 0; background: #b73333; color: #ffffff; }
.cancel-removal-button { border: 1px solid #d7ded9; background: #ffffff; color: #34423b; }

.save-bar {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px 24px;
  border-top: 1px solid #dfe5e1;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
}
.save-status { color: #6e7873; font-size: 0.9rem; }
.cancel-button, .review-button, .save-button { padding: 11px 18px; border: 0; border-radius: 9px; color: #ffffff; font: inherit; font-weight: 700; cursor: pointer; }
.cancel-button { background: #d64545; }
.review-button { background: #0a3550; }
.save-button { min-width: 145px; background: #18ab69; }
button:disabled { cursor: not-allowed; opacity: 0.55; }

@media (max-width: 780px) {
  .creator-actions { align-items: flex-start; flex-wrap: wrap; }
  .creator-buttons { margin-left: auto; }
  .field-grid { grid-template-columns: 1fr; }
  .full-width { grid-column: auto; }
}
</style>
